import {Controller} from '@hotwired/stimulus';

const availableSliderControllers = [];

export default class extends Controller {

    static get targets() {
        return ['slider', 'next', 'prev', 'dots'];
    }

    connect() {
        availableSliderControllers.push(this);
        const sliderContainer = this.element;
        const slider = sliderContainer.querySelector('.embla__viewport');
        const prevBtn = this.targets.find('prev');
        const nextBtn = this.targets.find('next');
        const dotsContainer = this.targets.find('dots');
        let embla;

        import('embla-carousel').then(({ default: emblaCarousel }) => {
            const additionalOptions = sliderContainer.dataset.slider ? JSON.parse(sliderContainer.dataset.slider) : {};
            // https://www.embla-carousel.com/api/options/
            const options = {
                loop: false,
                slidesToScroll: 'auto',
                ...additionalOptions,
            };

            embla = emblaCarousel(slider, options);

            if ('startAtEnd' in options) {
                const lastIndex = embla.slideNodes().length - 1;
                embla.scrollTo(lastIndex);

                embla.slideNodes().forEach((slide, index) => {
                    slide.addEventListener('click', () => centerSlide(index));
                });
            }

            if (prevBtn && nextBtn) {
                prevBtn.addEventListener('click', () => embla.scrollPrev());
                nextBtn.addEventListener('click', () => embla.scrollNext());

                function updateButtons() {
                    const selectedIndex = embla.selectedScrollSnap();
                    const lastIndex = embla.scrollSnapList().length - 1;

                    const atFirst = selectedIndex === 0;
                    const atLast = selectedIndex === lastIndex;

                    prevBtn.disabled = atFirst;
                    nextBtn.disabled = atLast;

                    const hideBoth = atFirst && atLast;
                    prevBtn.style.display = hideBoth ? 'none' : '';
                    nextBtn.style.display = hideBoth ? 'none' : '';
                }

                embla.on('select', updateButtons);
                updateButtons();
            }

            function centerSlide(index, animate = true) {
                const slide = embla.slideNodes()[index];
                if (!slide) return;

                const viewportWidth = slider.offsetWidth;

                const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
                const targetPx = slideCenter - viewportWidth / 2;

                const lastSlide = embla.slideNodes().slice(-1)[0];
                const trackWidth = lastSlide.offsetLeft + lastSlide.offsetWidth;
                const maxScroll = trackWidth - viewportWidth;

                const clampedTarget = Math.max(0, Math.min(targetPx, maxScroll));
                const snapList = embla.scrollSnapList();

                const targetProgress = clampedTarget / maxScroll;

                let closest = 0;
                let minDiff = Infinity;
                snapList.forEach((snap, i) => {
                    const diff = Math.abs(snap - targetProgress);
                    if (diff < minDiff) {
                        minDiff = diff;
                        closest = i;
                    }
                });

                embla.scrollTo(closest, animate);
            }

            if (dotsContainer) {
                const slidesCount = embla.scrollSnapList().length;

                if (slidesCount <= 1) {
                    dotsContainer.style.display = 'none';
                } else {
                    const dots = Array.from({ length: slidesCount }, (_, index) => {
                        const btn = document.createElement('button');
                        btn.className = 'embla__dot';
                        btn.addEventListener('click', () => embla.scrollTo(index));
                        dotsContainer.appendChild(btn);
                        return btn;
                    })

                    function updateActiveDot() {
                        const selectedIndex = embla.selectedScrollSnap();
                        dots.forEach((dot, i) =>
                            dot.classList.toggle('is-selected', i === selectedIndex)
                        )
                    }

                    embla.on('select', updateActiveDot);
                    updateActiveDot();
                }
            }

            bindSlideHoverEvents();
            embla.on('reInit', bindSlideHoverEvents);

            function bindSlideHoverEvents() {
                const slides = embla.slideNodes();

                slides.forEach((slide, index) => {
                    slide.removeEventListener('mouseenter', handleMouseEnter);
                    slide.removeEventListener('mouseleave', handleMouseLeave);

                    slide.addEventListener('mouseenter', handleMouseEnter);
                    slide.addEventListener('mouseleave', handleMouseLeave);
                });
            }

            function handleMouseEnter(event) {
                event.currentTarget.classList.add('active');
            }

            function handleMouseLeave(event) {
                event.currentTarget.classList.remove('active');
            }
        });
    }
}
